import type Store from '@ember-data/store';
import { concat } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import eq from 'ember-truth-helpers/helpers/eq';
import Cell from 'parog-web/components/bio-exam-results/summary/row/cell';
import { getNewExpectedDosages } from 'parog-web/components/bio-exam-results/utils/dosage-expected';
import getReferenceValuesMessage from 'parog-web/components/bio-exam-results/utils/reference-value-message';
import LinkToForc from 'parog-web/components/link-to-forc';
import type Patient from 'parog-web/models/patient';
import trimLoincCode from 'parog-web/utils/trim-loinc-code';
import type { CellBioExamResult, S as CellS } from './row/cell';

interface Args extends Pick<CellS['Args'], 'activeReferencesHook'> {
  code: string;
  patient: Patient;
  results: CellBioExamResult[];
  isImportantRow?: boolean;
}
export interface S {
  Args: Args;
}

export default class BioExamResultsSummaryRowComponent extends Component<S> {
  @service declare store: Store;

  get dosageChangedValueMessage(): string | undefined {
    const newExpectedDosages = getNewExpectedDosages(this.patient);
    if (
      newExpectedDosages == undefined ||
      newExpectedDosages[`LOINC-${this.#code}`] == undefined
    )
      return undefined;

    return (
      'T0 : ' +
      getReferenceValuesMessage({
        min: newExpectedDosages[`LOINC-${this.#code}`]?.minReferenceValue,
        max: newExpectedDosages[`LOINC-${this.#code}`]?.maxReferenceValue,
        prefix: ''
      })
    );
  }

  // NOTE: the legacy `.name-cell` styling was already dead before this
  // migration — `./index.module.css` resolves to the summary table CSS (which
  // has no `name-cell`), so `styles['name-cell']` was always undefined. We
  // preserve that behaviour here (no sticky / grey first cell).
  get firstCellStyle(): string {
    return this.args.isImportantRow ? '!bg-pink-light' : '';
  }

  /**
   * The label that should be displayed in the first cell of the row
   * */
  get label() {
    const record = this.store.peekRecord(
      'loinc',
      trimLoincCode(`LOINC-${this.args.code}`)
    );

    return record?.displayLabel;
  }

  get patient() {
    return this.args.patient;
  }

  get rowStyle(): string {
    return this.args.isImportantRow ? 'odd:!bg-pink-light even:!bg-white' : '';
  }

  get #code() {
    return this.args.code;
  }

  <template>
    <tr data-test-results-row={{concat 'LOINC-' @code}} class={{this.rowStyle}}>
      <td class={{this.firstCellStyle}}>
        <div data-test-label>
          {{this.label}}
        </div>

        {{#if (eq this.patient.therapeuticalArea.id 'nephro')}}
          {{#if this.dosageChangedValueMessage}}
            <div data-test-dosage-changed>
              {{this.dosageChangedValueMessage}}
              <LinkToForc
                @feature='bio-exam-monitoring'
                @component='edition-form'
                @patient={{@patient}}
                @sourceJeId={{@patient.bioExamExpectedDosageChangedJournalEvent.id}}
                @isReadOnly={{false}}
              >
                <i class='pencil alternate icon'></i>
              </LinkToForc>
            </div>
          {{/if}}
        {{/if}}

      </td>

      {{#each @results as |result|}}
        <Cell
          @cellResult={{result}}
          @activeReferencesHook={{@activeReferencesHook}}
        />
      {{/each}}
    </tr>
  </template>
}
